import { useState, useEffect, useRef } from 'react'
import { useTranslation } from "react-i18next";
import useMutation from 'hooks/useMutation'
import getDate from 'lib/getDate'
import makeid from 'lib/makeid'

function VitalSigns(props) {
  // Hallazgo real (loop de revisión, PR #367): este componente se monta en 5
  // lugares (Table.js, Form.js, Percentiles.js, Print.js, Consumers/EHR/EHR.js)
  // que no se excluyen entre sí (ej. el dropdown "+Signos Vitales" de Form.js
  // y el modal de Percentiles pueden coexistir en el DOM). Un id ESTÁTICO por
  // campo (necesario para los <label htmlFor> nuevos) colisionaría entre dos
  // instancias montadas a la vez — el label de una apuntaría al input de la
  // OTRA (getElementById toma el primer match). useRef con makeid() genera un
  // sufijo estable por instancia, una sola vez en el primer render.
  const instanceIdRef = useRef(null)
  if (!instanceIdRef.current) instanceIdRef.current = makeid(6)
  const fieldId = name => `vital-signs-${instanceIdRef.current}-${name}`

  const [state, setState] = useState({
    consumer: { id: props.consumer?.id, label: props.consumer?.label },
    createdBy: props.user?.uid,
    createdByName: props.resource?.label || props.user?.name,
    createdByResourceID: props.resource?.id,
    date: getDate(),
    startsAt: getDate(),
    ...props.value,
    type: 'vitalSigns'
  })
  const [mutate, mutation] = useMutation(window._l(`/records`).substr(1));
  const { t } = useTranslation()

  const onChange = ({ target: { name, value, type } }) => {
    state[name] = value
    setState({ ...state })
  }

  const onClose = () => {
    if (props.onClose) return props.onClose()
  }

  const save = () => {
    mutate(state, {
      onSuccess: record => {
        setState({ ...state, ...record })
        if (props.onSave) props.onSave(record)
      },
      // Bug real (auditoría 2026-08-14): sin onError, un guardado fallido no
      // avisaba nada — el botón volvía a "Guardar" en silencio y el
      // profesional podía creer que los signos vitales quedaron guardados.
      onError: error => {
        window.notify({
          message: error.message,
          type: "danger",
          title: "Error",
        });
      },
    })
  }

  return (
    <div
      className={[props.className, 'd-flex flex-column justify-content-top gap-2'].filter(Boolean).join(' ')}
      // Bug real (auditoría 2026-08-24, responsive): ancho fijo — este panel
      // se abre centrado dentro de una card con p-3 (32px de padding), así
      // que en un viewport angosto (ej. 320px) se recortaba contra los
      // bordes de la pantalla (el overlay padre tiene overflow-hidden, no
      // scroll). Se limita el ancho al espacio real disponible.
      //
      // Mejora real (Juan Cruz, card Percentiles 23/09): 300px dejaba las
      // columnas de inputs angostas — labels largos ("Perim Cefálico (cm)")
      // se cortaban con el ellipsis default de Bootstrap y las columnas no
      // usaban el ancho disponible. 400 le da más aire sin reabrir el bug
      // de 2026-08-24 (el min() sigue cediendo en viewports angostos).
      style={{ maxWidth: 'min(400px, calc(100vw - 32px))', width: 400 }}
    >
      {/* Card "Mejoras de UX en Percentiles" (22/09), punto 7: abierto desde
          Percentiles.js (props.simplified) se pide SOLO lo que alimenta el
          cálculo (peso/talla/PC) — TAS/TAD/FC/FR/T°/SatO2/FiO2/perím
          abdominal no aportan nada ahí y son pasos de carga innecesarios
          para ese flujo. El form general de Signos Vitales (fuera de
          Percentiles) sigue mostrando todo — esta prop no cambia nada ahí.
          Ocultar un campo no pierde el valor que ya tuviera en `state` (ver
          el spread de `props.value` arriba): editar un registro que YA
          tenía, por ejemplo, TAS cargado desde una consulta general sigue
          mandando ese valor al guardar, aunque acá no se muestre ni se
          pueda tocar. */}
      {/* Bug real (Juan Cruz, card Percentiles 23/09): estos campos usaban
          SOLO placeholder para indicar a qué dato corresponde cada input —
          un placeholder desaparece en cuanto el input tiene un valor
          cargado (comportamiento nativo del navegador), así que al abrir
          "Editar" sobre un registro ya cargado, todos los campos se veían
          en blanco sin ninguna etiqueta. `form-floating` (mismo patrón ya
          usado acá abajo para la fecha) muestra un `<label>` real que
          nunca desaparece, sin sacar el placeholder (Bootstrap lo sigue
          necesitando para animar el label). */}
      {!props.simplified && (
        <>
          <div className="d-flex gap-2">
            <div className="form-floating flex-fill">
              <input
                type="number"
                name="tas"
                id={fieldId("tas")}
                onChange={onChange}
                value={state.tas}
                placeholder={t("ehr:systolic_blood_pressure_short", 'TAS', { context: 'vital_signs' })}
                className="form-control"
              />
              <label htmlFor={fieldId("tas")}>{t("ehr:systolic_blood_pressure_short", 'TAS', { context: 'vital_signs' })}</label>
            </div>
            <div className="form-floating flex-fill">
              <input
                type="number"
                name="tad"
                id={fieldId("tad")}
                onChange={onChange}
                value={state.tad}
                placeholder={t("ehr:diastolic_blood_pressure_short", 'TAD', { context: 'vital_signs' })}
                className="form-control"
              />
              <label htmlFor={fieldId("tad")}>{t("ehr:diastolic_blood_pressure_short", 'TAD', { context: 'vital_signs' })}</label>
            </div>
          </div>
          <div className="d-flex gap-2">
            <div className="form-floating flex-fill">
              <input
                type="number"
                name="fc"
                id={fieldId("fc")}
                onChange={onChange}
                value={state.fc}
                placeholder={t("ehr:heart_rate_short", 'FC', { context: 'vital_signs' })}
                className="form-control"
              />
              <label htmlFor={fieldId("fc")}>{t("ehr:heart_rate_short", 'FC', { context: 'vital_signs' })}</label>
            </div>
            <div className="form-floating flex-fill">
              <input
                type="number"
                name="fr"
                id={fieldId("fr")}
                onChange={onChange}
                value={state.fr}
                placeholder={t("ehr:respiratory_rate_short", 'FR', { context: 'vital_signs' })}
                className="form-control"
              />
              <label htmlFor={fieldId("fr")}>{t("ehr:respiratory_rate_short", 'FR', { context: 'vital_signs' })}</label>
            </div>
          </div>
        </>
      )}

      <div className="d-flex gap-2">
        {!props.simplified && (
          <div className="flex-fill">
            <div className="form-floating">
              <input
                type="number"
                name="temp"
                id={fieldId("temp")}
                onChange={onChange}
                value={state.temp}
                placeholder={t("ehr:temp", 'T°', { context: 'vital_signs' })}
                className="form-control"
              />
              <label htmlFor={fieldId("temp")}>{t("ehr:temp", 'T°', { context: 'vital_signs' })}</label>
            </div>
            <div className="form-floating my-2">
              <input
                type="number"
                name="sat"
                id={fieldId("sat")}
                onChange={onChange}
                value={state.sat}
                placeholder={t("ehr:oxygen_saturation_shor", "SatO2", { context: 'vital_signs' })}
                className="form-control"
              />
              <label htmlFor={fieldId("sat")}>{t("ehr:oxygen_saturation_shor", "SatO2", { context: 'vital_signs' })}</label>
            </div>
            <div className="form-floating">
              <input
                type="number"
                name="fio2"
                id={fieldId("fio2")}
                onChange={onChange}
                value={state.fio2}
                placeholder={t("ehr:fraction_inspired_oxygen", 'FiO2', { context: 'vital_signs' })}
                className="form-control"
              />
              <label htmlFor={fieldId("fio2")}>{t("ehr:fraction_inspired_oxygen", 'FiO2', { context: 'vital_signs' })}</label>
            </div>
          </div>
        )}
        <div className="flex-fill">
          <div className="form-floating">
            <input
              type="number"
              name="weight"
              id={fieldId("weight")}
              onChange={onChange}
              value={state.weight}
              placeholder={t("ehr:weight_kg", 'Peso (kg)', { context: 'vital_signs' })}
              className="form-control"
            />
            <label htmlFor={fieldId("weight")}>{t("ehr:weight_kg", 'Peso (kg)', { context: 'vital_signs' })}</label>
          </div>
          <div className="form-floating my-2">
            <input
              type="number"
              name="height"
              id={fieldId("height")}
              onChange={onChange}
              value={state.height}
              placeholder={t("ehr:height_cm", 'Altura (cm)', { context: 'vital_signs' })}
              className="form-control"
            />
            <label htmlFor={fieldId("height")}>{t("ehr:height_cm", 'Altura (cm)', { context: 'vital_signs' })}</label>
          </div>
          {!props.simplified && (
            <div className="form-floating">
              <input
                type="number"
                name="abdperim"
                id={fieldId("abdperim")}
                onChange={onChange}
                value={state.abdperim}
                placeholder={t("ehr:abdominal_perimeter_cm", 'Perim Abd (cm)', { context: 'vital_signs' })}
                className="form-control"
              />
              <label htmlFor={fieldId("abdperim")}>{t("ehr:abdominal_perimeter_cm", 'Perim Abd (cm)', { context: 'vital_signs' })}</label>
            </div>
          )}
          <div className="form-floating mt-2">
            {/* Perímetro cefálico (0-5 años): alimenta la 4ta curva OMS de
                percentiles. Mismo criterio de nombre que abdperim. */}
            <input
              type="number"
              name="headperim"
              id={fieldId("headperim")}
              onChange={onChange}
              value={state.headperim}
              placeholder={t("ehr:head_perimeter_cm", 'Perim Cefálico (cm)', { context: 'vital_signs' })}
              className="form-control"
            />
            <label htmlFor={fieldId("headperim")}>{t("ehr:head_perimeter_cm", 'Perim Cefálico (cm)', { context: 'vital_signs' })}</label>
          </div>
        </div>

      </div>
      <div className="form-floating">
        <input
          type="date"
          placeholder={t("ehr:date", { context: 'vital_signs' })}
          className="form-control"
          onChange={onChange}
          name="startsAt"
          value={state.startsAt}
        />
        <label>{t("ehr:date", { context: 'vital_signs' })}</label>
      </div>
      <div className="d-flex justify-content-between">
        {mutation.isLoading ? (
          <button disabled className="btn btn-link mx-n2">{t('ehr:saving', { context: 'vital_signs' })}</button>
        ) : (
          <button disabled={!state.tas && !state.tad && !state.fc && !state.fr && !state.temp && !state.sat && !state.fio2 && !state.weight && !state.height && !state.abdperim && !state.headperim} className="btn btn-link mx-n2" onClick={save}>{t('ehr:save', { context: 'vital_signs' })}</button>
        )}
        <button className="btn link-secondary mx-n2" onClick={onClose}>{t('close')}</button>
      </div>
    </div>
  )
}

export default VitalSigns
